import keyBy from 'lodash/keyBy';
import uniq from 'lodash/uniq';
import fromPairs from 'lodash/fromPairs';
import get from 'lodash/get';
import mapValues from 'lodash/mapValues';
import isArray from 'lodash/isArray';
import escapeRegExp from 'lodash/escapeRegExp';
import Logger from 'observability/Logger';

import {
    LOGIC_PROPERTY_VISIBLE,
    LOGIC_OPERAND_TYPE_QUESTION,
    LOGIC_OPERAND_TYPE_GROUP,
    LOGIC_OPERAND_TYPE_CONTENT,
    LOGIC_OPERAND_TYPE_STRING,
    LOGIC_OPERATOR_GREATER_THAN,
    LOGIC_OPERATOR_LESS_THAN,
    LOGIC_OPERATOR_EQUALS,
    LOGIC_OPERATOR_NOT_EQUALS,
    LOGIC_OPERATOR_EQUALS_ANY,
    LOGIC_OPERATOR_NOT_EQUALS_ANY,
    LOGIC_OPERATOR_REGEX,
    LOGIC_OPERATOR_CONTAINS,
    LOGIC_OPERATOR_NOT_CONTAINS,
    MULTIPLE_OPERATOR_SEPARATOR,
} from 'common/entities/formLogicRule/constants';
import { INPUT_TYPE_MULTI_SELECT } from 'common/entities/formNode/inputTypes';

const LOG = new Logger('getNodesVisibility');

const FORMATTING_VALUE_FUNCTION_PER_INPUT_TYPE = {
    [INPUT_TYPE_MULTI_SELECT]: (value) => value && value.split(MULTIPLE_OPERATOR_SEPARATOR),
};

/**
 * Depending on operantType, will resolve to the value associated with the given operand or return
 * the operand itself.
 * @param  {string} operand
 * @param  {string} operandType
 * @param  {Object} values - { [nodeId]: value, ... }
 * @return {string}
 */
const getOperandValue = (operand, operandType, values) => {
    switch (operandType) {
        case LOGIC_OPERAND_TYPE_QUESTION:
            return values[operand];
        case LOGIC_OPERAND_TYPE_STRING:
        default:
            return operand;
    }
};

/**
 * Creates a regular expression from a suppleid operand
 * @param  {string} operand - string representing a regular expression
 * @return {RegExp} regexp
 */
export function createRegExpFromOperand(operand) {
    try {
        if (operand instanceof RegExp) {
            return operand;
        }

        if (typeof operand !== 'string') {
            return new RegExp();
        }

        const matches = /^\s*\/(.*)\/(\w*)\s*$/.exec(operand);
        if (!matches) {
            return new RegExp(operand);
        }

        const [, pattern, flags] = matches;
        // @NOTE: Consider adding 'u' and 'y' flags eventually? Filtering them for safety now.
        return new RegExp(
            pattern,
            uniq(flags)
                .join('')
                .replace(/[^gim]/g, ''),
        );
        // Malformed regexps will throw exceptions.
    } catch (err) {
        return null;
    }
}

/**
 * Check if the value introduced in the input field matches the saved logic as a regexp.
 * @param {string} valueToCompare
 * @param {string|number} baseValues
 * @return {string}
 */
export const equalsAny = (valueToCompare, baseValues) => {
    const valueToString = baseValues.toString().trim();
    const matchText = valueToString
        ? valueToString
              .split(MULTIPLE_OPERATOR_SEPARATOR)
              .map((value) => escapeRegExp(value))
              .join('|')
        : '\\s';
    const regexp = new RegExp(`^(${matchText})$`, 'i');

    if (Array.isArray(valueToCompare)) {
        return valueToCompare.map((next) => regexp.test(next)).filter(Boolean).length > 0;
    }

    return regexp.test(valueToCompare);
};

const DECIMAL_NUMBER_REGEXP = /^\d*\.?\d+$/;

const isNumeric = (number) =>
    typeof number === 'number' || (typeof number === 'string' && DECIMAL_NUMBER_REGEXP.test(number));

const checkIfFalsyButNotFalse = (value) => value === '' || value === null || value === undefined;

const checkBothValuesFalsy = (a, b) => checkIfFalsyButNotFalse(a) && checkIfFalsyButNotFalse(b);
const checkBothValuesEqualAndNumeric = (a, b) => isNumeric(a) && isNumeric(b) && a == b; // eslint-disable-line eqeqeq
// For getboxes, false equals not responded.
const checkIsOneValueFalseAndAnotherFalsy = (a, b) =>
    (a === false && checkIfFalsyButNotFalse(b)) || (b === false && checkIfFalsyButNotFalse(a));
const checkBothValuesStrictlyEqual = (a, b) => a === b;
const checkSingleItemEquality = (a, b) => isArray(a) && a.length === 1 && checkBothValuesStrictlyEqual(a[0], b);

const isEqual = (a, b) => {
    const areBothValuesFalsy = checkBothValuesFalsy(a, b);
    const areBothValuesEqualAndNumeric = checkBothValuesEqualAndNumeric(a, b);
    const isOneValueFalseAndAnotherFalsy = checkIsOneValueFalseAndAnotherFalsy(a, b);
    const areBothValuesStrictlyEqual = checkBothValuesStrictlyEqual(a, b);
    const isSingleItemEqual = checkSingleItemEquality(a, b);

    return (
        areBothValuesFalsy ||
        areBothValuesEqualAndNumeric ||
        isOneValueFalseAndAnotherFalsy ||
        areBothValuesStrictlyEqual ||
        isSingleItemEqual
    );
};

/**
 * Checks if the user input contains any of the values from valueToCompare
 * valueToCompare is composed from other strings separated my `||`
 * @param {string} inputString
 * @param {string} valueToCompare
 * @return {bool}
 */
const contains = (inputString, valueToCompare) => {
    const valuesToCompare = valueToCompare.split(MULTIPLE_OPERATOR_SEPARATOR);

    return (
        inputString !== null &&
        inputString !== undefined &&
        !!valuesToCompare.find((value) => inputString.toString().includes(value))
    );
};

/** @var {Object.<function>} */
const evaluateOperatorFunctions = {
    [LOGIC_OPERATOR_EQUALS]: (a, b) => isEqual(a, b), // eslint-disable-line eqeqeq
    [LOGIC_OPERATOR_NOT_EQUALS]: (a, b) => !isEqual(a, b), // eslint-disable-line eqeqeq
    [LOGIC_OPERATOR_EQUALS_ANY]: (a, b) => equalsAny(a, b),
    [LOGIC_OPERATOR_NOT_EQUALS_ANY]: (a, b) => !equalsAny(a, b),
    [LOGIC_OPERATOR_CONTAINS]: (a, b) => contains(a, b),
    [LOGIC_OPERATOR_NOT_CONTAINS]: (a, b) => !contains(a, b),
    [LOGIC_OPERATOR_GREATER_THAN]: (a, b) => {
        const parsedA = isNumeric(a) ? parseInt(a, 10) : a;
        const parsedB = isNumeric(b) ? parseInt(b, 10) : b;
        return parsedA > parsedB;
    },
    [LOGIC_OPERATOR_LESS_THAN]: (a, b) => {
        const parsedA = isNumeric(a) ? parseInt(a, 10) : a;
        const parsedB = isNumeric(b) ? parseInt(b, 10) : b;
        return parsedA < parsedB;
    },
    [LOGIC_OPERATOR_REGEX]: (a, b) => {
        const regExp = createRegExpFromOperand(b);
        return regExp && regExp.test(a);
    },
};

/**
 * @param  {mixed} operandLeft
 * @param  {string} operator
 * @param  {mixed} operandRight
 * @return {bool}
 */
export const evaluateCondition = (operandLeft, operator, operandRight) => {
    const evaluator = evaluateOperatorFunctions[operator];

    if (!evaluator) {
        LOG.warn('validation, unknown operator type', operator);
        return false;
    }

    return evaluator(operandLeft, operandRight);
};

/**
 * @param  {Object} formLogicRule - entity
 * @param  {Object} values - { [nodeId]: value, ... }
 * @return {bool}
 */
export const evaluateVisibleCondition = (formLogicRule, values, formLogicRules) => {
    const { operandLeft, operandLeftType, operandRight, operandRightType, operator } = formLogicRule;

    const operandLeftValue = getOperandValue(operandLeft, operandLeftType, values);
    const operandRightValue = getOperandValue(operandRight, operandRightType, values);

    const relatedFormLogicRules = formLogicRules.filter((flRule) => flRule.target === formLogicRule.operandLeft);

    const filteredFormLogicRules = formLogicRules.filter((flRule) => flRule.target !== formLogicRule.operandLeft);

    let visible = evaluateCondition(operandLeftValue, operator, operandRightValue);

    if (relatedFormLogicRules.length && visible !== false) {
        relatedFormLogicRules.forEach((newFormRule) => {
            // Only apply rules if they are correctly defined using the modal.
            if (newFormRule.operandLeft && newFormRule.operator && newFormRule.operandRight) {
                // Ensures that all the questions that were based on answers
                // to subsequent questions are hidden
                visible = evaluateVisibleCondition(newFormRule, values, filteredFormLogicRules);
            }
        });
    }

    return visible;
};

/**
 * @param  {Object} formNodesById - form node entities by id.
 * @param  {string} parentNodeId
 * @return {string[]} - array containing ids of all children found in sub tree.
 */
const getAllNodeChildren = (formNodesById, parentNodeId) => {
    const parentNode = formNodesById[parentNodeId];
    const nodeIds = [...(parentNode.children || [])];
    const allChildrenIds = [];

    while (nodeIds.length) {
        const nodeId = nodeIds.pop();
        allChildrenIds.push(nodeId);

        const node = formNodesById[nodeId];
        nodeIds.push(...(node.children || []));
    }

    return allChildrenIds;
};

const getNodeValuesFromType = (nodesByType, values) =>
    Object.keys(values).reduce((acc, next) => {
        const nodeType = nodesByType[next];
        const formattedValue =
            !!FORMATTING_VALUE_FUNCTION_PER_INPUT_TYPE[nodeType] && values[next]
                ? FORMATTING_VALUE_FUNCTION_PER_INPUT_TYPE[nodeType](values[next])
                : values[next];

        return {
            ...acc,
            [next]: formattedValue,
        };
    }, {});

/**
 * @param {Array} entities
 * @return {boolean}
 */
export const getEntitiesVisibility = (entities) =>
    !!Object.values(entities).find((entity) => entity.available > 0 || typeof entity.available === 'undefined');

/**
 * Checks if a node has visible options.
 * @param {Object} node
 * @return {boolean}
 */
export const getNodeOptionsVisibility = (node) => {
    const hideNotAvailableResponses = get(node.config, 'hideNotAvailableResponses', false);

    const nodeEntities = get(node.config, 'options.entities', {});
    const hasVisibleEntities = getEntitiesVisibility(nodeEntities);

    return hideNotAvailableResponses ? hasVisibleEntities : true;
};

export const getNodesWithOptionsVisibility = (formNodes) => {
    const nodesWithOptions = formNodes.filter((node) => node.config && node.config.options);

    return nodesWithOptions.reduce(
        (acc, node) => ({
            ...acc,
            [node.id]: getNodeOptionsVisibility(node),
        }),
        {},
    );
};

/**
 * Merges both visibility objects considering that the nodes with both true sources or
 * a true + undefined source (which means it has no options) is going to be visible.
 * @param {Object} nodeVisibilityDependingOnLogicRulesById
 * @param {Object} nodesWithOptionsVisibility
 * @return {Object}
 */
export const mergeNodesVisibilityWithOptions = (nodeVisibilityDependingOnLogicRulesById, nodesWithOptionsVisibility) =>
    mapValues(nodeVisibilityDependingOnLogicRulesById, (isNodeVisibleByRules, key) => {
        const isNodeVisibileByOptions = get(nodesWithOptionsVisibility, key, true);
        return isNodeVisibleByRules && isNodeVisibileByOptions;
    });

/**
 * @param  {Array} formNodes - entities
 * @param  {Array} formLogicRules - entities
 * @param  {Object} values - { [nodeId]: value, ... }
 * @return {Object} - { [nodeId]: true|false, ... }
 */
export default function getNodesVisibility(formNodes, formLogicRules, values) {
    const formNodesById = keyBy(formNodes, 'id');
    const nodesAllVisible = fromPairs(formNodes.map((formNode) => [formNode.id, true]));
    const nodeTypeById = formNodes.reduce(
        (acc, { id, inputType }) => ({
            ...acc,
            [id]: inputType,
        }),
        {},
    );

    const visibleFormLogicRules = formLogicRules.filter(
        (formLogicRule) =>
            formLogicRule.property === LOGIC_PROPERTY_VISIBLE &&
            (formLogicRule.targetType === LOGIC_OPERAND_TYPE_QUESTION ||
                formLogicRule.targetType === LOGIC_OPERAND_TYPE_GROUP ||
                formLogicRule.targetType === LOGIC_OPERAND_TYPE_CONTENT),
    );

    const nodesWithOptionsVisibility = getNodesWithOptionsVisibility(formNodes);

    const nodeVisibilityDependingOnLogicRulesById = visibleFormLogicRules.reduce((memo, formLogicRule) => {
        const { target: targetId } = formLogicRule;

        // If node already flagged as not visible, skip it.
        if (!memo[targetId]) {
            return memo;
        }

        const nodeValues = getNodeValuesFromType(nodeTypeById, values);
        const visible = evaluateVisibleCondition(formLogicRule, nodeValues, formLogicRules);

        // Need to flag all children as not visible too.
        const childrenVisibility = !visible
            ? fromPairs(getAllNodeChildren(formNodesById, targetId).map((id) => [id, false]))
            : {};

        return {
            ...memo,
            [targetId]: !!visible,
            ...childrenVisibility,
        };
    }, nodesAllVisible);

    return mergeNodesVisibilityWithOptions(nodeVisibilityDependingOnLogicRulesById, nodesWithOptionsVisibility);
}
